import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Play, Square, Plus, User, Trash2, Folder, FolderOpen, ChevronRight, FolderPlus } from "lucide-react";

function Ticker({ start }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const secs = Math.max(0, Math.floor((now - new Date(start).getTime()) / 1000));
  const h = String(Math.floor(secs / 3600)).padStart(2, "0");
  const m = String(Math.floor((secs % 3600) / 60)).padStart(2, "0");
  const s = String(secs % 60).padStart(2, "0");
  return <span className="font-mono font-bold text-emerald-600">{h}:{m}:{s}</span>;
}

export default function TimeTracker() {
  const { user } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [companyFilter, setCompanyFilter] = useState("all");
  const [employees, setEmployees] = useState([]);
  const [active, setActive] = useState([]);
  const [entries, setEntries] = useState([]);
  const [manualOpen, setManualOpen] = useState(false);
  const [manual, setManual] = useState({ employee_id: "", date: new Date().toISOString().slice(0, 10), hours: 8, note: "" });
  const [groups, setGroups] = useState([]);
  const [openFolders, setOpenFolders] = useState({});
  const [groupOpen, setGroupOpen] = useState(false);
  const [newGroup, setNewGroup] = useState({ name: "", company_id: "" });

  const scopeQ = user?.role === "admin" && companyFilter !== "all" ? `?company_id=${companyFilter}` : "";

  const load = useCallback(() => {
    api.get(`/employees${scopeQ}`).then((r) => setEmployees(r.data)).catch(() => {});
    api.get(`/time/active${scopeQ}`).then((r) => setActive(r.data)).catch(() => {});
    api.get(`/time${scopeQ}`).then((r) => setEntries(r.data)).catch(() => {});
    api.get(`/groups${scopeQ}`).then((r) => setGroups(r.data)).catch(() => {});
  }, [scopeQ]);

  useEffect(() => { api.get("/companies").then((r) => setCompanies(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const activeMap = Object.fromEntries(active.map((a) => [a.employee_id, a]));

  const clockIn = async (emp) => {
    try {
      await api.post("/time/clock-in", { employee_id: emp.id });
      toast.success(`${emp.name} clocked in`);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const clockOut = async (emp) => {
    try {
      const { data } = await api.post("/time/clock-out", { employee_id: emp.id });
      toast.success(`${emp.name} clocked out · ${data.hours}h logged`);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const toggleFolder = (name) => setOpenFolders((f) => ({ ...f, [name]: !f[name] }));

  const openGroupDialog = () => {
    const defaultCompany = user?.role === "manager" ? user.company_id
      : (companyFilter !== "all" ? companyFilter : companies[0]?.id || "");
    setNewGroup({ name: "", company_id: defaultCompany });
    setGroupOpen(true);
  };

  const createGroup = async (e) => {
    e.preventDefault();
    if (!newGroup.name.trim()) return toast.error("Enter a group name");
    if (!newGroup.company_id) return toast.error("Select a company");
    try {
      await api.post("/groups", { name: newGroup.name.trim(), company_id: newGroup.company_id });
      toast.success("Group created");
      setGroupOpen(false);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const deleteGroup = async (g) => {
    if (!window.confirm(`Delete "${g.name}"? Employees in it will become Unassigned.`)) return;
    try {
      await api.delete(`/groups/${g.id}`);
      toast.success("Group deleted");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const addManual = async (e) => {
    e.preventDefault();
    if (!manual.employee_id) return toast.error("Select an employee");
    try {
      await api.post("/time/manual", { ...manual, hours: Number(manual.hours) });
      toast.success("Timesheet entry added");
      setManualOpen(false);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const removeEntry = async (id) => { await api.delete(`/time/${id}`); load(); };

  return (
    <div className="animate-fade-up">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-sky-600">Attendance</p>
          <h1 className="font-display text-3xl font-extrabold text-slate-900 mt-1">Time Tracker</h1>
          <p className="text-slate-500 mt-1">Clock employees in and out. Hours feed the weekly payroll.</p>
        </div>
        <div className="flex items-center gap-3">
          {user?.role === "admin" && (
            <Select value={companyFilter} onValueChange={setCompanyFilter}>
              <SelectTrigger className="w-48" data-testid="time-company-filter"><SelectValue placeholder="All companies" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All companies</SelectItem>
                {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <Button onClick={openGroupDialog} data-testid="new-group-button" variant="outline" className="rounded-full font-semibold border-slate-300"><FolderPlus className="w-4 h-4 mr-1" /> New group</Button>
          <Button onClick={() => setManualOpen(true)} data-testid="add-manual-time-button" variant="outline" className="rounded-full font-semibold border-slate-300"><Plus className="w-4 h-4 mr-1" /> Manual entry</Button>
        </div>
      </div>

      <div className="mb-10 space-y-4">
        {employees.length === 0 && groups.length === 0 && <p className="text-slate-400 text-sm">No employees to track. Create a group and assign employees to it.</p>}
        {(() => {
          const byGroup = {};
          employees.forEach((e) => { const g = e.group || "Unassigned"; (byGroup[g] = byGroup[g] || []).push(e); });
          const names = Array.from(new Set([...groups.map((g) => g.name), ...Object.keys(byGroup)]));
          names.sort((a, b) => (a === "Unassigned" ? 1 : b === "Unassigned" ? -1 : a.localeCompare(b)));
          return names.map((name) => {
            const list = byGroup[name] || [];
            const activeCount = list.filter((e) => activeMap[e.id]).length;
            const isOpen = !!openFolders[name];
            const groupDoc = groups.find((g) => g.name === name && (user?.role !== "admin" ? g.company_id === user?.company_id : (companyFilter === "all" || g.company_id === companyFilter)));
            return (
              <div key={name} className="rounded-2xl border border-slate-200 bg-white overflow-hidden" data-testid={`group-folder-${name}`}>
                <button type="button" onClick={() => toggleFolder(name)} data-testid={`group-folder-toggle-${name}`}
                  className="w-full flex items-center justify-between gap-3 px-5 py-4 hover:bg-slate-50 transition-colors text-left">
                  <div className="flex items-center gap-3">
                    <ChevronRight className={`w-4 h-4 text-slate-400 transition-transform ${isOpen ? "rotate-90" : ""}`} />
                    {isOpen ? <FolderOpen className="w-5 h-5 text-sky-600" /> : <Folder className="w-5 h-5 text-sky-600" />}
                    <span className="font-display font-bold text-lg text-slate-900">{name}</span>
                    <span className="px-2.5 py-1 rounded-full bg-slate-100 text-slate-600 text-xs font-semibold">{list.length} {list.length === 1 ? "employee" : "employees"}</span>
                    {activeCount > 0 && <span className="px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 text-xs font-semibold">{activeCount} on shift</span>}
                  </div>
                  {name !== "Unassigned" && groupDoc && (
                    <span onClick={(ev) => { ev.stopPropagation(); deleteGroup(groupDoc); }} data-testid={`delete-group-${name}`}
                      className="p-2 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition-colors" title="Delete group">
                      <Trash2 className="w-4 h-4" />
                    </span>
                  )}
                </button>
                {isOpen && (
                  <div className="px-5 pb-5 border-t border-slate-100">
                    {list.length === 0
                      ? <p className="text-slate-400 text-sm py-4">No employees in this group yet. Assign employees to it on the Employees page.</p>
                      : <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-4">
                          {list.map((emp) => {
                            const shift = activeMap[emp.id];
                            return (
                              <div key={emp.id} data-testid={`time-card-${emp.id}`}
                                className={`rounded-2xl border bg-white p-5 transition-all ${shift ? "border-emerald-200 shadow-lg shadow-emerald-100/50" : "border-slate-100"}`}>
                                <div className="flex items-center gap-3 mb-4">
                                  {emp.avatar_url ? <img src={emp.avatar_url} alt="" className="w-10 h-10 rounded-full object-cover" />
                                    : <div className="w-10 h-10 rounded-full bg-sky-50 text-sky-600 flex items-center justify-center"><User className="w-5 h-5" /></div>}
                                  <div className="min-w-0">
                                    <p className="font-semibold text-slate-800 truncate">{emp.name}</p>
                                    <p className="text-xs text-slate-500 truncate">{emp.position || "—"}</p>
                                  </div>
                                </div>
                                <div className="flex items-center justify-between">
                                  <div className="text-sm">
                                    {shift
                                      ? <span className="inline-flex items-center gap-2 text-emerald-600 font-semibold"><span className="w-2 h-2 rounded-full bg-emerald-500 pulse-ring" /> On shift · <Ticker start={shift.clock_in} /></span>
                                      : <span className="text-slate-400">Off shift</span>}
                                  </div>
                                  {shift
                                    ? <Button size="sm" onClick={() => clockOut(emp)} data-testid={`clock-out-button-${emp.id}`} className="bg-rose-600 hover:bg-rose-700 rounded-full"><Square className="w-3.5 h-3.5 mr-1" /> Clock out</Button>
                                    : <Button size="sm" onClick={() => clockIn(emp)} data-testid={`clock-in-button-${emp.id}`} className="bg-emerald-600 hover:bg-emerald-700 rounded-full"><Play className="w-3.5 h-3.5 mr-1" /> Clock in</Button>}
                                </div>
                              </div>
                            );
                          })}
                        </div>}
                  </div>
                )}
              </div>
            );
          });
        })()}
      </div>

      <div className="rounded-2xl border border-slate-100 bg-white overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100"><h2 className="font-display font-bold text-lg">Recent timesheet entries</h2></div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left font-semibold px-6 py-3">Employee</th>
              <th className="text-left font-semibold px-6 py-3">Date</th>
              <th className="text-left font-semibold px-6 py-3">Hours</th>
              <th className="text-left font-semibold px-6 py-3">Source</th>
              <th className="px-6 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {entries.filter((e) => e.clock_out).length === 0 && (
              <tr><td colSpan={5} className="px-6 py-10 text-center text-slate-400">No completed entries yet.</td></tr>
            )}
            {entries.filter((e) => e.clock_out).slice(0, 40).map((e) => (
              <tr key={e.id} data-testid={`time-entry-${e.id}`} className="hover:bg-slate-50 transition-colors">
                <td className="px-6 py-3 font-semibold text-slate-800">{e.employee_name}</td>
                <td className="px-6 py-3 text-slate-600">{new Date(e.clock_in).toLocaleDateString()}</td>
                <td className="px-6 py-3 font-mono text-slate-700">{e.hours}h</td>
                <td className="px-6 py-3"><span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 text-xs font-semibold capitalize">{e.source}</span></td>
                <td className="px-6 py-3 text-right"><button onClick={() => removeEntry(e.id)} className="p-1.5 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition-colors"><Trash2 className="w-4 h-4" /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={manualOpen} onOpenChange={setManualOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Manual timesheet entry</DialogTitle><DialogDescription>Log worked hours for an employee on a specific date.</DialogDescription></DialogHeader>
          <form onSubmit={addManual} className="space-y-4">
            <div>
              <Label>Employee</Label>
              <Select value={manual.employee_id} onValueChange={(v) => setManual({ ...manual, employee_id: v })}>
                <SelectTrigger className="mt-1.5" data-testid="manual-employee-select"><SelectValue placeholder="Select employee" /></SelectTrigger>
                <SelectContent>{employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Date</Label><Input data-testid="manual-date-input" type="date" value={manual.date} onChange={(e) => setManual({ ...manual, date: e.target.value })} className="mt-1.5" /></div>
              <div><Label>Hours</Label><Input data-testid="manual-hours-input" type="number" step="0.25" value={manual.hours} onChange={(e) => setManual({ ...manual, hours: e.target.value })} className="mt-1.5 font-mono" /></div>
            </div>
            <div><Label>Note</Label><Input data-testid="manual-note-input" value={manual.note} onChange={(e) => setManual({ ...manual, note: e.target.value })} className="mt-1.5" /></div>
            <DialogFooter><Button type="submit" data-testid="save-manual-time-button" className="bg-sky-600 hover:bg-sky-700">Add entry</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={groupOpen} onOpenChange={setGroupOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Create a group</DialogTitle><DialogDescription>Employees you assign to this group will clock in inside its folder.</DialogDescription></DialogHeader>
          <form onSubmit={createGroup} className="space-y-4">
            {user?.role === "admin" && (
              <div>
                <Label>Company</Label>
                <Select value={newGroup.company_id} onValueChange={(v) => setNewGroup({ ...newGroup, company_id: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="new-group-company-select"><SelectValue placeholder="Select company" /></SelectTrigger>
                  <SelectContent>{companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label>Group name</Label>
              <Input data-testid="new-group-name-input" value={newGroup.name} onChange={(e) => setNewGroup({ ...newGroup, name: e.target.value })} placeholder="e.g. Group A" className="mt-1.5" />
            </div>
            <DialogFooter><Button type="submit" data-testid="save-group-button" className="bg-sky-600 hover:bg-sky-700">Create group</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
